import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import FacebookOutlinedIcon from '@mui/icons-material/FacebookOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { usePartnerCompanyInfoQuery } from '../../../api/use-partner-company-info-query';
import { useUpdatePartnerCompanyInfoMutation } from '../../../api/use-update-partner-company-info-mutation';
import { useRegeneratePartnerAvatarMutation } from '../../../api/use-regenerate-partner-avatar-mutation';
import { useRegeneratePartnerCoverBannerMutation } from '../../../api/use-regenerate-partner-cover-banner-mutation';
import { mergePartnerProfileWithCompanyInfo } from '../../../services/partnerCompanyInfo';
import { isPartnerCommunityCreated } from '../../Partner/Community/partnerCommunityAccess';
import { isBlobPreviewUrl, revokeBlobPreviewUrl } from '../../Events/ManageEvent/coverImageUtils';
import { PARTNER_BRAND } from '../../Partner/partnerDashboardTheme';
import CompanyMediaField from './CompanyMediaField';
import CompanyInfoLabel from './CompanyInfoLabel';
import SocialLinkField from './SocialLinkField';
import { usePartnerMediaOptimization } from './usePartnerMediaOptimization';
import { normalizeWhatsAppCommunityLink } from '../../../utils/whatsappCommunityLink';
import {
  ABOUT_DESCRIPTION_MAX,
  PARTNER_COVER_OUTPUT_HINT,
  PARTNER_LOGO_OUTPUT_HINT,
  PARTNER_MEDIA_SIZE_HINT,
  SHORT_DESCRIPTION_MAX,
  createCompanyInfoInitialState,
  getCompanyInfoSaveErrorMessage,
  getCompanyMediaSubmitBlockMessage,
  validateCompanyInfo,
} from './companyInfoDefaults';
import { COMPANY_INFO_FIELD_SX, companyInfoPrimaryButtonSx } from './companyInfoStyles';

const InstagramSocialIcon = ({ sx }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...sx,
    }}
  >
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  </Box>
);

const CharacterCount = ({ value, max }) => (
  <Typography
    sx={{
      display: 'block',
      textAlign: 'right',
      mt: 0.5,
      mb: 2.75,
      fontSize: '0.71875rem',
      color: '#B0AAA6',
    }}
  >
    {value.length}/{max}
  </Typography>
);

const CompanyInfo = ({ generatedPatch, onGeneratedConsumed, onLiveChange }) => {
  const { isPartner } = useAuth();
  const {
    data: partnerUser,
    isLoading: isCompanyInfoLoading,
    isError: isCompanyInfoError,
    error: companyInfoError,
  } = usePartnerCompanyInfoQuery();
  const communityCreated = isPartnerCommunityCreated(partnerUser);

  const [savedSnapshot, setSavedSnapshot] = useState(() => createCompanyInfoInitialState(null));
  const [form, setForm] = useState(savedSnapshot);
  const [errors, setErrors] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);
  const hydratedPartnerIdRef = useRef(null);

  useEffect(() => {
    if (!partnerUser?.id) return;
    if (hydratedPartnerIdRef.current === partnerUser.id) return;

    const next = createCompanyInfoInitialState(partnerUser);
    setSavedSnapshot(next);
    setForm(next);
    setErrors({});
    hydratedPartnerIdRef.current = partnerUser.id;
    setIsHydrated(true);
  }, [partnerUser]);

  useEffect(() => {
    if (!isHydrated) return;
    onLiveChange?.(form);
  }, [form, isHydrated, onLiveChange]);

  useEffect(() => {
    if (!generatedPatch) return;
    setForm((prev) => ({ ...prev, ...generatedPatch }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(generatedPatch).forEach((field) => {
        delete next[field];
      });
      return next;
    });
    onGeneratedConsumed?.();
  }, [generatedPatch, onGeneratedConsumed]);

  const updateMutation = useUpdatePartnerCompanyInfoMutation({
    onSuccess: (result) => {
      const mergedUser = result.partnerProfile
        ? mergePartnerProfileWithCompanyInfo(partnerUser, result.partnerProfile)
        : partnerUser;
      const nextSnapshot = createCompanyInfoInitialState(mergedUser);
      setSavedSnapshot(nextSnapshot);
      setForm(nextSnapshot);
      setErrors({});
      toast.success(result.message || 'Partner company info updated successfully');
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to update company info');
    },
  });

  const regeneratePartnerAvatarMutation = useRegeneratePartnerAvatarMutation();
  const regeneratePartnerCoverBannerMutation = useRegeneratePartnerCoverBannerMutation();

  const handleLogoMediaChange = useCallback(({ file, preview, removed }) => {
    setForm((prev) => ({
      ...prev,
      logoFile: file,
      logoPreview: preview || '',
      logoRemoved: Boolean(removed),
    }));
    setErrors((prev) => {
      if (!prev.logo) return prev;
      const next = { ...prev };
      delete next.logo;
      return next;
    });
  }, []);

  const handleCoverMediaChange = useCallback(({ file, preview, removed }) => {
    setForm((prev) => ({
      ...prev,
      coverFile: file,
      coverPreview: preview || '',
      coverRemoved: Boolean(removed),
    }));
    setErrors((prev) => {
      if (!prev.cover) return prev;
      const next = { ...prev };
      delete next.cover;
      return next;
    });
  }, []);

  const logoOptimization = usePartnerMediaOptimization({
    file: form.logoFile,
    preview: form.logoPreview,
    onChange: handleLogoMediaChange,
    regenerateMutation: regeneratePartnerAvatarMutation,
    noun: 'Logo',
    toastId: 'partner-logo-optimization',
  });

  const coverOptimization = usePartnerMediaOptimization({
    file: form.coverFile,
    preview: form.coverPreview,
    onChange: handleCoverMediaChange,
    regenerateMutation: regeneratePartnerCoverBannerMutation,
    noun: 'Cover banner',
    toastId: 'partner-cover-optimization',
  });

  const mediaSubmitBlockMessage = getCompanyMediaSubmitBlockMessage(
    form,
    {
      optimizing: logoOptimization.optimizing,
      pendingUpload: logoOptimization.pendingUpload,
      optimizationError: logoOptimization.optimizationError,
    },
    {
      optimizing: coverOptimization.optimizing,
      pendingUpload: coverOptimization.pendingUpload,
      optimizationError: coverOptimization.optimizationError,
    },
  );

  const logoInitial = useMemo(() => {
    const name = form.fullName || partnerUser?.fullName || 'P';
    return name.charAt(0).toUpperCase();
  }, [form.fullName, partnerUser?.fullName]);

  const logoPreview = form.logoPreview || undefined;
  const coverPreview = form.coverPreview || '';
  const isSubmitting = updateMutation.isPending;

  const isDirty = useMemo(() => {
    if (form.logoFile || form.coverFile) return true;
    if (form.logoRemoved !== savedSnapshot.logoRemoved) return true;
    if (form.coverRemoved !== savedSnapshot.coverRemoved) return true;

    const socialOrCopyChanged =
      form.fullName !== savedSnapshot.fullName ||
      form.shortDescription !== savedSnapshot.shortDescription ||
      form.description !== savedSnapshot.description ||
      form.logoPreview !== savedSnapshot.logoPreview ||
      form.coverPreview !== savedSnapshot.coverPreview ||
      form.websiteUrl !== savedSnapshot.websiteUrl ||
      form.instagramUrl !== savedSnapshot.instagramUrl ||
      form.facebookUrl !== savedSnapshot.facebookUrl;

    if (socialOrCopyChanged) return true;
    if (!communityCreated) return false;
    return form.whatsappUrl !== savedSnapshot.whatsappUrl;
  }, [communityCreated, form, savedSnapshot]);

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleShortDescriptionChange = (event) => {
    const value = event.target.value.slice(0, SHORT_DESCRIPTION_MAX);
    updateField('shortDescription', value);
  };

  const handleAboutChange = (event) => {
    const value = event.target.value.slice(0, ABOUT_DESCRIPTION_MAX);
    updateField('description', value);
  };

  const handleSave = () => {
    if (!isPartner) {
      toast.error('Partner access is required to update company info.');
      return;
    }

    if (mediaSubmitBlockMessage) {
      toast.error(mediaSubmitBlockMessage);
      return;
    }

    const validationErrors = validateCompanyInfo(form, partnerUser, { communityCreated });
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      toast.error(getCompanyInfoSaveErrorMessage(validationErrors));
      return;
    }

    updateMutation.mutate({ form });
  };

  if (isCompanyInfoLoading && !partnerUser) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={32} sx={{ color: PARTNER_BRAND }} />
      </Box>
    );
  }

  if (isCompanyInfoError) {
    return (
      <Typography sx={{ fontSize: '0.875rem', color: '#6B6560' }}>
        {companyInfoError?.message || 'Failed to load company info'}
      </Typography>
    );
  }

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        handleSave();
      }}
    >
      <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: 'text.primary', mb: 2.75 }}>
        Manage your community&apos;s public identity across SureSpace
      </Typography>

      <CompanyInfoLabel htmlFor="company-display-name" required>
        Display Name
      </CompanyInfoLabel>
      <TextField
        id="company-display-name"
        fullWidth
        required
        value={form.fullName}
        onChange={(e) => updateField('fullName', e.target.value)}
        error={Boolean(errors.fullName)}
        helperText={errors.fullName}
        disabled={isSubmitting}
        sx={{ ...COMPANY_INFO_FIELD_SX, mb: errors.fullName ? 1 : 2.75 }}
      />

      <CompanyInfoLabel htmlFor="company-short-description" required>
        Short Description
      </CompanyInfoLabel>
      <TextField
        id="company-short-description"
        fullWidth
        required
        multiline
        minRows={2}
        maxRows={2}
        value={form.shortDescription}
        onChange={handleShortDescriptionChange}
        error={Boolean(errors.shortDescription)}
        helperText={errors.shortDescription}
        disabled={isSubmitting}
        inputProps={{ maxLength: SHORT_DESCRIPTION_MAX }}
        sx={COMPANY_INFO_FIELD_SX}
      />
      <CharacterCount value={form.shortDescription} max={SHORT_DESCRIPTION_MAX} />

      <CompanyInfoLabel htmlFor="company-about" required>
        About Description
      </CompanyInfoLabel>
      <TextField
        id="company-about"
        fullWidth
        required
        multiline
        minRows={4}
        maxRows={4}
        value={form.description}
        onChange={handleAboutChange}
        error={Boolean(errors.description)}
        helperText={errors.description}
        disabled={isSubmitting}
        inputProps={{ maxLength: ABOUT_DESCRIPTION_MAX }}
        sx={COMPANY_INFO_FIELD_SX}
      />
      <CharacterCount value={form.description} max={ABOUT_DESCRIPTION_MAX} />

      <CompanyMediaField
        label="Logo"
        required
        variant="logo"
        previewUrl={logoPreview}
        previewAlt={form.fullName || partnerUser?.fullName || 'Company logo'}
        placeholderLetter={logoInitial}
        formatHint={PARTNER_MEDIA_SIZE_HINT}
        outputHint={PARTNER_LOGO_OUTPUT_HINT}
        error={Boolean(errors.logo)}
        helperText={errors.logo}
        optimizing={logoOptimization.optimizing}
        optimizationError={logoOptimization.optimizationError}
        onImageSelected={logoOptimization.handleSelected}
        onCancelOptimization={logoOptimization.cancelOptimization}
        uploadDisabled={isSubmitting}
        deleteDisabled={!logoPreview || isSubmitting}
        onDelete={() => {
          if (isBlobPreviewUrl(form.logoPreview)) {
            revokeBlobPreviewUrl(form.logoPreview);
          }
          handleLogoMediaChange({ file: null, preview: '', removed: true });
        }}
      />

      <CompanyMediaField
        label="Cover Banner"
        required
        variant="banner"
        previewUrl={coverPreview}
        formatHint={PARTNER_MEDIA_SIZE_HINT}
        outputHint={PARTNER_COVER_OUTPUT_HINT}
        error={Boolean(errors.cover)}
        helperText={errors.cover}
        optimizing={coverOptimization.optimizing}
        optimizationError={coverOptimization.optimizationError}
        onImageSelected={coverOptimization.handleSelected}
        onCancelOptimization={coverOptimization.cancelOptimization}
        uploadDisabled={isSubmitting}
        deleteDisabled={!coverPreview || isSubmitting}
        onDelete={() => {
          if (isBlobPreviewUrl(form.coverPreview)) {
            revokeBlobPreviewUrl(form.coverPreview);
          }
          handleCoverMediaChange({ file: null, preview: '', removed: true });
        }}
      />

      <CompanyInfoLabel>Links & Socials</CompanyInfoLabel>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 3.25 }}>
        <SocialLinkField
          icon={LanguageOutlinedIcon}
          value={form.websiteUrl}
          onChange={(e) => updateField('websiteUrl', e.target.value)}
          placeholder="https://yourcommunity.com"
          disabled={isSubmitting}
          error={Boolean(errors.websiteUrl)}
          helperText={errors.websiteUrl}
        />
        <SocialLinkField
          icon={InstagramSocialIcon}
          value={form.instagramUrl}
          onChange={(e) => updateField('instagramUrl', e.target.value)}
          placeholder="https://instagram.com/yourcommunity"
          disabled={isSubmitting}
          error={Boolean(errors.instagramUrl)}
          helperText={errors.instagramUrl}
        />
        <SocialLinkField
          icon={FacebookOutlinedIcon}
          value={form.facebookUrl}
          onChange={(e) => updateField('facebookUrl', e.target.value)}
          placeholder="https://facebook.com/yourcommunity"
          disabled={isSubmitting}
          error={Boolean(errors.facebookUrl)}
          helperText={errors.facebookUrl}
        />
      </Box>

      <CompanyInfoLabel>WhatsApp Community Link</CompanyInfoLabel>
      <Box
        sx={{
          mb: 3.25,
          opacity: communityCreated ? 1 : 0.55,
        }}
      >
        <SocialLinkField
          icon={WhatsAppIcon}
          value={form.whatsappUrl}
          onChange={(e) =>
            updateField('whatsappUrl', normalizeWhatsAppCommunityLink(e.target.value))
          }
          placeholder="https://chat.whatsapp.com/your-community-link"
          disabled={isSubmitting || !communityCreated}
          error={Boolean(errors.whatsappUrl)}
          helperText={
            errors.whatsappUrl ||
            (!communityCreated ? 'Available after your community is created.' : undefined)
          }
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          disableElevation
          disabled={isSubmitting || !isDirty || Boolean(mediaSubmitBlockMessage)}
          sx={companyInfoPrimaryButtonSx}
        >
          {isSubmitting ? 'Saving…' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};

export default CompanyInfo;
